import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useUserAuth } from "../../hooks/useUserAuth";
import { createLoginSchema, LoginFormData } from "../../helpers/userValidation";

const LoginForm: React.FC = () => {
  const { t } = useTranslation();
  const { handleAuth, isLoading } = useUserAuth();
  const forgotPasswordUrl = window.commonVariables?.forgotPasswordUrl || "#";

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<LoginFormData>({
    resolver: zodResolver(createLoginSchema(t)),
    defaultValues: {
      remember: true, // Set the checkbox to be checked by default
    },
  });

  // --- TIMESTAMP LOGIC ---
  useEffect(() => {
    // Register the field with react-hook-form
    register("form_load_timestamp");
    // Set its value to the current time in seconds
    setValue("form_load_timestamp", Math.floor(Date.now() / 1000));
  }, [register, setValue]);
  // --- END TIMESTAMP ---

  const onSubmit = async (data: LoginFormData) => {
    const response = await handleAuth({
      type: "login",
      ...data,
      remember: data.remember ?? false, // Ensure 'remember' is always a boolean, defaulting to false.
    });

    if (response.success) {
      const myAccountUrl = window.commonVariables?.myAccountUrl;
      if (myAccountUrl) {
        // Redirect directly to the My Account page.
        window.location.href = myAccountUrl;
      } else {
        // Fallback to reload if the URL isn't available for some reason.
        window.location.reload();
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="auth-form" noValidate>
      {/* --- HONEYPOT FIELD --- */}
      <div className="honeypot-field" aria-hidden="true">
        <label htmlFor="user_nickname_login">Nickname</label>
        <input
          id="user_nickname_login"
          type="text"
          {...register("nickname")}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>
      {/* --- END HONEYPOT --- */}

      <div className="form-field">
        <label htmlFor="username">{t("user.auth.usernameLabel")}</label>
        <input
          id="username"
          type="text"
          {...register("username")}
          autoComplete="username"
          className={errors.username ? "has-error" : ""}
        />
        {errors.username && (
          <p className="form-field__error">{errors.username.message}</p>
        )}
      </div>
      <div className="form-field">
        <label htmlFor="password">{t("user.auth.passwordLabel")}</label>
        <input
          id="password"
          type="password"
          {...register("password")}
          autoComplete="current-password"
          className={errors.password ? "has-error" : ""}
        />
        {errors.password && (
          <p className="form-field__error">{errors.password.message}</p>
        )}
      </div>

      <div
        className="form-footer-actions"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div className="form-field form-field--checkbox" style={{ margin: 0 }}>
          <label htmlFor="remember-me">
            <input id="remember-me" type="checkbox" {...register("remember")} />
            <span>{t("user.auth.rememberMe", "Remember me")}</span>
          </label>
        </div>

        <a href={forgotPasswordUrl} className="auth-link">
          {t("user.auth.forgotPassword", "Forgot Password?")}
        </a>
      </div>

      <button type="submit" disabled={isLoading} style={{ marginTop: "1rem" }}>
        {isLoading
          ? t("user.auth.loginButtonLoading")
          : t("user.auth.loginButton")}
      </button>
    </form>
  );
};

export default LoginForm;
